import React, { useMemo } from 'react';

import { MenuItem, MenuList, Typography } from '@mui/material';
import { AlineCheckbox } from 'src/components';
import { SearchInput } from 'src/components/SearchInput';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  checkbox: {
    padding: theme.spacing(0.5),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  emptyState: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(3, 2),
    textAlign: 'center',
  },
  menuItem: {
    gap: theme.spacing(0.75),
    minHeight: 36,
    padding: theme.spacing(0.5, 1),
  },
  menuItemSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  optionsList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  searchContainer: {
    flexShrink: 0,
    marginBottom: theme.spacing(1),
  },
}));

interface FilterOption {
  [key: string]: unknown;
  id: string;
  label: string;
}

interface FilterOptionsListProps<T extends FilterOption> {
  filteredOptions: T[];
  onToggle: (optionId: string) => void;
  searchTerm: string;
  selectedIds: Set<string>;
  showCheckbox: boolean;
  showSearch: boolean;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
}

function FilterOptionsList<T extends FilterOption>({
  filteredOptions,
  onToggle,
  renderOption,
  searchTerm,
  selectedIds,
  showCheckbox,
  showSearch,
}: FilterOptionsListProps<T>): JSX.Element {
  const { classes } = useStyles();

  const defaultRenderOption = (option: T, _isSelected: boolean) => (
    <Typography variant="body2">{option.label}</Typography>
  );

  if (filteredOptions.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="body2">
          {showSearch && searchTerm ? 'No results found' : 'No options available'}
        </Typography>
      </div>
    );
  }

  return (
    <>
      {filteredOptions.map(option => {
        const isSelected = selectedIds.has(option.id);
        return (
          <MenuItem
            key={option.id}
            className={classes.menuItem}
            selected={isSelected}
            onClick={e => {
              e.stopPropagation();
              onToggle(option.id);
            }}
          >
            {showCheckbox && (
              <AlineCheckbox
                checked={isSelected}
                classes={{ root: classes.checkbox }}
                density="compact"
              />
            )}
            {renderOption
              ? renderOption(option, isSelected)
              : defaultRenderOption(option, isSelected)}
          </MenuItem>
        );
      })}
    </>
  );
}

interface FilterPopoverContentProps<T extends FilterOption = FilterOption> {
  onToggle: (optionId: string) => void;
  options: T[];
  selectedIds: Set<string>;
  children?: React.ReactNode;
  onSearchChange?: (search: string) => void;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  searchPlaceholder?: string;
  searchTerm?: string;
  showCheckbox?: boolean;
  showSearch?: boolean;
}

export function FilterPopoverContent<T extends FilterOption = FilterOption>({
  children,
  onSearchChange,
  onToggle,
  options,
  renderOption,
  searchPlaceholder = 'Search...',
  searchTerm = '',
  selectedIds,
  showCheckbox = true,
  showSearch = false,
}: FilterPopoverContentProps<T>): JSX.Element {
  const { classes } = useStyles();

  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchTerm.trim()) return options;
    return options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm, showSearch]);

  return (
    <div className={classes.container}>
      {showSearch && onSearchChange && (
        <div className={classes.searchContainer}>
          <SearchInput
            placeholder={searchPlaceholder}
            search={searchTerm}
            setSearch={onSearchChange}
          />
        </div>
      )}

      <MenuList disablePadding className={classes.optionsList}>
        <FilterOptionsList
          filteredOptions={filteredOptions}
          renderOption={renderOption}
          searchTerm={searchTerm}
          selectedIds={selectedIds}
          showCheckbox={showCheckbox}
          showSearch={showSearch}
          onToggle={onToggle}
        />
      </MenuList>

      {children}
    </div>
  );
}
